/**
 * Wizard de apontamento de PLACAS CONTRAN (Fase 2).
 * Mobile-first. 7 passos:
 *  1) Equipe + OS + Item (categoria Vertical)
 *  2) Categoria CONTRAN (R/A/I/E/T)
 *  3) Código da placa (grid filtrado)
 *  4) Tamanho (padrão ou livre) + qtd de placas iguais
 *  5) Tipo de suporte
 *  6) Localização (km + lado) + foto + GPS
 *  7) Revisão + salvar
 */
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { enqueue } from "@/lib/offlineQueue";
import OfflineDataBanner from "@/components/OfflineDataBanner";
import { loadScaledCanvas, canvasToCompressedJpeg, dataUrlBytes, fmtKB } from "@/lib/imageCompress";
import { PageHeader, itemDaCategoria, nomeItem } from "@/components/pcp";
import { Camera, CheckCircle, X, MapPin, ArrowLeft, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  CATEGORIAS_CONTRAN,
  getTamanhosPadrao,
  SUPORTES,
  LADOS_VIA,
  PLACAS_DESENHO,
} from "@/data/contranSigns";

// ------- utilidades -------
function normalizeKm(v) {
  return String(v || "").replace(/[^\d+]/g, "");
}

// Cálculo local só para preview (backend recalcula de forma autoritativa)
function calcAreaUnit(placa, dims) {
  if (!placa) return 0;
  const toM = (v) => (parseFloat(v || 0) || 0) / 100.0;
  switch (placa.forma) {
    case "circulo":
      return (Math.PI * toM(dims.diametro_cm) ** 2) / 4.0;
    case "octogono":
      return 2 * (1 + Math.SQRT2) * toM(dims.lado_cm) ** 2;
    case "triangulo":
      return (Math.sqrt(3) / 4) * toM(dims.lado_cm) ** 2;
    case "losango":
      return toM(dims.lado_cm) ** 2;
    default:
      return toM(dims.largura_cm) * toM(dims.altura_cm);
  }
}

// Miniatura SVG para o botão do código — dá contexto visual sem precisar de PNG
function ThumbForma({ forma, cor, corFundo, size = 44 }) {
  const s = size;
  const stroke = cor || "#0A0A0A";
  const fill = corFundo || "#FFF";
  switch (forma) {
    case "circulo":
      return (
        <svg width={s} height={s} viewBox="0 0 44 44">
          <circle cx="22" cy="22" r="18" fill={fill} stroke={stroke} strokeWidth="3" />
        </svg>
      );
    case "octogono":
      return (
        <svg width={s} height={s} viewBox="0 0 44 44">
          <polygon points="14,4 30,4 40,14 40,30 30,40 14,40 4,30 4,14" fill={fill} stroke={stroke} strokeWidth="3" />
        </svg>
      );
    case "triangulo":
      return (
        <svg width={s} height={s} viewBox="0 0 44 44">
          <polygon points="22,40 4,8 40,8" fill={fill} stroke={stroke} strokeWidth="3" />
        </svg>
      );
    case "losango":
      return (
        <svg width={s} height={s} viewBox="0 0 44 44">
          <polygon points="22,4 40,22 22,40 4,22" fill={fill} stroke={stroke} strokeWidth="3" />
        </svg>
      );
    default:
      return (
        <svg width={s} height={s} viewBox="0 0 44 44">
          <rect x="4" y="12" width="36" height="20" fill={fill} stroke={stroke} strokeWidth="3" />
        </svg>
      );
  }
}

export const TIPOS_FOTO = [
  { key: "antes", label: "Antes" },
  { key: "durante", label: "Durante" },
  { key: "depois", label: "Depois" },
  { key: "verso", label: "Verso" },
  { key: "carimbo", label: "Carimbo" },
];

// Desenho real da placa (padrão CONTRAN) em /placas/{codigo}.svg; se não existir, cai na forma genérica
const PLACAS_COM_DESENHO = new Set(PLACAS_DESENHO);
export function PlacaImg({ placa, cor, corFundo, size = 44 }) {
  const [erro, setErro] = useState(false);
  if (erro || !PLACAS_COM_DESENHO.has(placa.codigo)) return <ThumbForma forma={placa.forma} cor={cor} corFundo={corFundo} size={size} />;
  return <img src={`/placas/${placa.codigo}.svg`} alt={`${placa.codigo} — ${placa.nome}`} width={size} height={size} loading="lazy" onError={() => setErro(true)}
    style={{ width: size, height: size, objectFit: "contain", background: "#FFF", borderRadius: 3, flexShrink: 0 }} data-testid={`placa-img-${placa.codigo}`} />;
}

export default function ApontamentoPlacas() {
  const [step, setStep] = useState(1);
  const [ordens, setOrdens] = useState([]);
  const [equipes, setEquipes] = useState([]);
  const [itens, setItens] = useState([]);

  // Passo 1
  const [selectedEquipe, setSelectedEquipe] = useState("");
  const [selectedOs, setSelectedOs] = useState("");
  const [selectedItem, setSelectedItem] = useState("");
  // Passo 2
  const [categoria, setCategoria] = useState(null); // objeto de CATEGORIAS_CONTRAN
  // Passo 3
  const [placa, setPlaca] = useState(null); // objeto {codigo, nome, forma}
  const [filtro, setFiltro] = useState("");
  // Passo 4
  const [tamanhoPadrao, setTamanhoPadrao] = useState(""); // nome do preset ou "Livre"
  const [dims, setDims] = useState({ diametro_cm: "", lado_cm: "", largura_cm: "", altura_cm: "" });
  const [qtdPlacas, setQtdPlacas] = useState("1");
  // Passo 5
  const [suporte, setSuporte] = useState(null);
  // Poste/suporte: item da OS que recebe o apontamento derivado (medição separada)
  const [posteItem, setPosteItem] = useState("");      // "" = não apontar
  const [qtdPostes, setQtdPostes] = useState("1");
  const POSTES_POR_SUPORTE = { poste_simples: 1, poste_duplo: 2, bandeira: 1, semi_portico: 1, portico: 1 };
  const isItemPoste = (i) => /poste|suporte|p[óo]rtico|coluna|bandeira/i.test(i.descricao || "");
  const itensPoste = itens.filter((i) => i.id !== selectedItem && !i.deleted);
  const escolherSuporte = (s) => {
    setSuporte(s);
    setQtdPostes(String(POSTES_POR_SUPORTE[s.key] || 1));
    if (!posteItem) { const sug = itensPoste.find(isItemPoste); if (sug) setPosteItem(sug.id); }
  };
  // Passo 6
  const [km, setKm] = useState("");
  const [ladoVia, setLadoVia] = useState(null);
  const [fotos, setFotos] = useState([]);          // data-urls
  const [fotosTipos, setFotosTipos] = useState([]); // tipo de cada foto (mesma ordem): antes | depois | verso | carimbo
  const [tipoFotoAtual, setTipoFotoAtual] = useState("antes");
  const [geo, setGeo] = useState(null);
  const [geoLoading, setGeoLoading] = useState(false);
  // Passo 7
  const [observacao, setObservacao] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/ordens").then((r) =>
      setOrdens(r.data.filter((o) => o.status !== "Concluída" && o.status !== "Cancelada"))
    );
    api.get("/equipes").then((r) => setEquipes(r.data));
  }, []);

  useEffect(() => {
    setSelectedOs(""); setSelectedItem(""); setItens([]);
  }, [selectedEquipe]);

  useEffect(() => {
    if (!selectedOs) { setItens([]); setSelectedItem(""); return; }
    setSelectedItem("");
    api.get(`/itens?os_id=${selectedOs}`).then((r) => {
      setItens(r.data.filter((i) => itemDaCategoria(i, "vertical")));
    });
  }, [selectedOs]);

  // OS filtradas pela equipe
  const ordensDaEquipe = ordens.filter((o) => !selectedEquipe || o.equipe_id === selectedEquipe);
  const currentItem = itens.find((i) => i.id === selectedItem);

  // Presets de tamanho para a placa escolhida
  const presetsTamanho = useMemo(() => getTamanhosPadrao(placa), [placa]);

  // Cálculo local (preview)
  const areaUnit = useMemo(() => calcAreaUnit(placa, dims), [placa, dims]);
  const qtdN = parseFloat(qtdPlacas) || 0;
  const areaTotal = areaUnit * qtdN;

  const dimsPreenchidas = () => {
    if (!placa) return false;
    switch (placa.forma) {
      case "circulo":   return parseFloat(dims.diametro_cm) > 0;
      case "octogono":
      case "triangulo":
      case "losango":   return parseFloat(dims.lado_cm) > 0;
      default:          return parseFloat(dims.largura_cm) > 0 && parseFloat(dims.altura_cm) > 0;
    }
  };

  const aplicarPreset = (nome, valores) => {
    setTamanhoPadrao(nome);
    setDims({ diametro_cm: "", lado_cm: "", largura_cm: "", altura_cm: "", ...valores });
  };

  const captureGeo = () => {
    if (!("geolocation" in navigator)) { toast.error("GPS não disponível"); return; }
    setGeoLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setGeo({ latitude: pos.coords.latitude, longitude: pos.coords.longitude, accuracy: pos.coords.accuracy });
        setGeoLoading(false);
        toast.success(`Localização OK (±${Math.round(pos.coords.accuracy)}m)`);
      },
      (err) => {
        setGeoLoading(false);
        toast.error(err.code === 1 ? "Permissão de GPS negada" : "Falha ao obter GPS");
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 60000 }
    );
  };

  const handlePhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    const tipo = e.target.dataset.tipo || tipoFotoAtual;
    if (files.length === 0) return;
    toast.info(`${files.length} arquivo(s) selecionado(s) · processando…`);
    let currentGeo = geo;
    if (!currentGeo && "geolocation" in navigator) {
      try {
        currentGeo = await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(
            (pos) => resolve({ latitude: pos.coords.latitude, longitude: pos.coords.longitude, accuracy: pos.coords.accuracy }),
            reject,
            { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
          );
        });
        setGeo(currentGeo);
      } catch { /* segue sem GPS */ }
    }
    let ok = 0;
    for (const f of files) {
      try {
        const stamped = await stampImage(f, currentGeo, tipo);
        setFotos((p) => [...p, stamped]);
        setFotosTipos((p) => [...p, tipo]);
        ok++;
      } catch (err) {
        toast.error(`Falha ao processar ${f.name}: ${err?.message || err}`);
      }
    }
    if (ok > 0) toast.success(`${ok} foto(s) prontas com timestamp`);
    // limpa o input pra permitir escolher o mesmo arquivo de novo se precisar
    e.target.value = "";
  };

  // Timestamp Canvas — mesma lógica da Pintura, adaptada p/ placa
  const stampImage = async (file, geoData, tipoFoto) => {
          const canvas = await loadScaledCanvas(file);
          const ctx = canvas.getContext("2d");

          const W = canvas.width;
          const H = canvas.height;
          const now = new Date();
          const pad2 = (n) => String(n).padStart(2, "0");
          const dt = `${pad2(now.getDate())}/${pad2(now.getMonth() + 1)}/${now.getFullYear()} ${pad2(now.getHours())}:${pad2(now.getMinutes())}`;

          const fmtCoord = (v, pos, neg) => {
            const abs = Math.abs(v).toFixed(6).replace(".", ",");
            return `${abs}${v < 0 ? neg : pos}`;
          };
          const gpsText = geoData
            ? `${fmtCoord(geoData.latitude, "N", "S")} ${fmtCoord(geoData.longitude, "E", "W")}`
            : "GPS: nao capturado";

          const categoriaLabel = "SINALIZAÇÃO VERTICAL";
          const placaLabel = placa ? (placa.sem_codigo ? placa.nome : `${placa.codigo} · ${placa.nome}`).toUpperCase() : "";
          const kmLabel = km ? `KM ${km}${ladoVia ? " · " + (ladoVia.label || ladoVia).toUpperCase() : ""}` : (ladoVia ? (ladoVia.label || ladoVia).toUpperCase() : "");

          const tipoLabel = TIPOS_FOTO.find((t) => t.key === tipoFoto)?.label?.toUpperCase();
          const linhas = [dt, gpsText, categoriaLabel, placaLabel, kmLabel, tipoLabel].filter(Boolean);

          const fontSize = Math.max(22, Math.round(H * 0.033));
          const lineHeight = Math.round(fontSize * 1.15);
          const pad = Math.round(fontSize * 0.6);

          ctx.font = `bold ${fontSize}px Arial, sans-serif`;
          ctx.textBaseline = "bottom";
          ctx.textAlign = "left";

          let y = H - pad;
          for (let i = linhas.length - 1; i >= 0; i--) {
            const txt = linhas[i];
            ctx.lineWidth = Math.max(3, Math.round(fontSize * 0.18));
            ctx.strokeStyle = "rgba(0,0,0,0.85)";
            ctx.strokeText(txt, pad, y);
            ctx.fillStyle = "#FFFFFF";
            ctx.fillText(txt, pad, y);
            y -= lineHeight;
          }
          return canvasToCompressedJpeg(canvas);
  };

  const missingReason = () => {
    if (step === 1) {
      if (!selectedEquipe) return "Escolha a equipe";
      if (!selectedOs) return "Escolha a Ordem de Serviço";
      if (!selectedItem) return "Escolha o item de placa";
    }
    if (step === 2 && !categoria) return "Escolha o grupo CONTRAN";
    if (step === 3 && !placa) return "Escolha o código da placa";
    if (step === 4) {
      if (!dimsPreenchidas()) return "Preencha as dimensões";
      if (!(qtdN > 0)) return "Informe a quantidade de placas";
    }
    if (step === 5) {
      if (!suporte) return "Escolha o tipo de suporte";
    }
    if (step === 6) {
      if (!km) return "Digite o km";
      if (!ladoVia) return "Escolha o lado da via";
      if (fotos.length === 0) return "Tire pelo menos a foto do DEPOIS (placa implantada)";
      if (!fotosTipos.includes("depois")) return "A foto do DEPOIS é obrigatória (2º espaço)";
    }
    return "";
  };

  const canNext = () => {
    if (step === 1) return selectedEquipe && selectedOs && selectedItem;
    if (step === 2) return !!categoria;
    if (step === 3) return !!placa;
    if (step === 4) return dimsPreenchidas() && qtdN > 0;
    if (step === 5) return !!suporte;
    if (step === 6) return !!km && !!ladoVia && fotosTipos.includes("depois");
    return true;
  };

  const submit = async () => {
    if (!currentItem || !placa || !categoria) return;
    setLoading(true);
    try {
      const payload = {
        os_id: selectedOs,
        item_id: selectedItem,
        equipe_id: selectedEquipe || null,
        quantidade: areaTotal, // backend sobrescreve com base na unidade do item
        observacao,
        fotos,
        fotos_tipos: fotosTipos,
        data_execucao: new Date().toISOString(),
        latitude: geo?.latitude ?? null,
        longitude: geo?.longitude ?? null,
        gps_accuracy: geo?.accuracy ?? null,
        placa: {
          codigo: placa.codigo,
          nome: placa.nome,
          categoria: categoria.key,
          forma: placa.forma,
          tamanho_padrao: tamanhoPadrao || "Livre",
          diametro_cm: parseFloat(dims.diametro_cm) || null,
          lado_cm: parseFloat(dims.lado_cm) || null,
          largura_cm: parseFloat(dims.largura_cm) || null,
          altura_cm: parseFloat(dims.altura_cm) || null,
          qtd_placas: qtdN,
          tipo_suporte: suporte?.label || null,
          km,
          lado_via: ladoVia?.key || ladoVia,
        },
      };

      const nPostes = posteItem ? Math.max(1, parseInt(qtdPostes, 10) || 1) : 0;
      const payloadPoste = nPostes ? {
        os_id: selectedOs, item_id: posteItem, equipe_id: selectedEquipe || null,
        quantidade: nPostes, fotos, data_execucao: payload.data_execucao,
        latitude: payload.latitude, longitude: payload.longitude, gps_accuracy: payload.gps_accuracy,
        observacao: `Suporte da ${placa.sem_codigo ? placa.nome.toLowerCase() : "placa " + placa.codigo} (${suporte?.label}) · km ${km}${observacao ? " · " + observacao : ""}`,
        origem: "suporte_placa",
        suporte_de: { codigo: placa.codigo, km, tipo_suporte: suporte?.label || null, qtd_placas: qtdN },
      } : null;
      const isNetworkError = (e) => !navigator.onLine || !e?.response || e?.code === "ERR_NETWORK";
      const sufixo = nPostes ? ` + ${nPostes} poste${nPostes > 1 ? "s" : ""}` : "";
      try {
        await api.post("/apontamentos", payload);
        if (payloadPoste) await api.post("/apontamentos", payloadPoste);
        toast.success(`${placa.sem_codigo ? placa.nome : "Placa " + placa.codigo} apontada · ${areaTotal.toFixed(2)} m²${sufixo}`);
      } catch (err) {
        if (isNetworkError(err)) {
          await enqueue("/apontamentos", payload);
          if (payloadPoste) await enqueue("/apontamentos", payloadPoste);
          toast.info(`Sem internet · ${placa.sem_codigo ? placa.nome : "Placa " + placa.codigo}${sufixo} salva no celular. Envia sozinho quando voltar o sinal.`);
        } else {
          throw err;
        }
      }
      // Reset (mantém equipe/OS/item para o operador seguir apontando placas)
      setStep(2);
      setCategoria(null); setPlaca(null); setFiltro("");
      setTamanhoPadrao(""); setDims({ diametro_cm: "", lado_cm: "", largura_cm: "", altura_cm: "" });
      setQtdPlacas("1"); setSuporte(null);
      setKm(""); setLadoVia(null); setFotos([]); setFotosTipos([]); setTipoFotoAtual("antes"); setGeo(null); setObservacao("");
      if (navigator.onLine) {
        try {
          const r = await api.get(`/itens?os_id=${selectedOs}`);
          setItens(r.data.filter((i) => itemDaCategoria(i, "vertical")));
        } catch { /* silent */ }
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || "";
      toast.error(`Erro ao salvar${detail ? ": " + detail : ""}`);
    } finally {
      setLoading(false);
    }
  };

  const placasFiltradas = useMemo(() => {
    if (!categoria) return [];
    const q = filtro.trim().toLowerCase();
    if (!q) return categoria.placas;
    return categoria.placas.filter(
      (p) => p.codigo.toLowerCase().includes(q) || p.nome.toLowerCase().includes(q)
    );
  }, [categoria, filtro]);

  return (
    <>
      <PageHeader
        testid="page-apontamento-placas"
        title="Apontamento de Placas"
        subtitle={`Passo ${step} de 7 — catálogo CONTRAN`}
      />
      <div className="page-body" style={{ maxWidth: 640, margin: "0 auto", width: "100%", paddingBottom: 96 }}>
        <OfflineDataBanner />
        {/* Barra de progresso */}
        <div style={{ display: "flex", gap: 4, marginBottom: 20 }}>
          {[1,2,3,4,5,6,7].map((n) => (
            <div key={n} style={{
              flex: 1, height: 6, borderRadius: 3,
              background: n <= step ? "#0A0A0A" : "#E4E4E7",
              transition: "background 0.2s",
            }} />
          ))}
        </div>

        {/* PASSO 1 — Equipe + OS + Item */}
        {step === 1 && (
          <div className="pcp-card" style={{ padding: 20 }}>
            <div className="overline mb-1">Passo 1</div>
            <h2 className="font-display font-bold text-lg" style={{ marginBottom: 16 }}>Qual OS e serviço?</h2>
            <div style={{ display: "grid", gap: 16 }}>
              <div>
                <label className="label">Equipe <span style={{ color: "#DC2626" }}>*</span></label>
                <select className="select input-lg" value={selectedEquipe} onChange={(e) => setSelectedEquipe(e.target.value)} data-testid="ap-pl-equipe">
                  <option value="">— Selecione a equipe —</option>
                  {equipes.map((eq) => <option key={eq.id} value={eq.id}>{eq.nome}</option>)}
                </select>
              </div>
              <div>
                <label className="label">Ordem de Serviço <span style={{ color: "#DC2626" }}>*</span></label>
                <select className="select input-lg" value={selectedOs} onChange={(e) => setSelectedOs(e.target.value)} disabled={!selectedEquipe} data-testid="ap-pl-os">
                  <option value="">{selectedEquipe ? "— Selecione a OS —" : "— Selecione a equipe primeiro —"}</option>
                  {ordensDaEquipe.map((o) => <option key={o.id} value={o.id}>{o.numero} · {o.contratante} · {o.rodovia}</option>)}
                </select>
                {selectedEquipe && ordensDaEquipe.length === 0 && (
                  <div style={{ fontSize: "0.8rem", color: "#B45309", marginTop: 8 }}>
                    Não há OS ativas designadas a esta equipe. Vincule uma OS em <strong>Ordens de Serviço</strong>.
                  </div>
                )}
              </div>
              <div>
                <label className="label">Item de placa (categoria Vertical) <span style={{ color: "#DC2626" }}>*</span></label>
                <select className="select input-lg" value={selectedItem} onChange={(e) => setSelectedItem(e.target.value)} disabled={!selectedOs} data-testid="ap-pl-item">
                  <option value="">{selectedOs ? "— Selecione o item —" : "— Selecione a OS primeiro —"}</option>
                  {itens.map((i) => <option key={i.id} value={i.id}>{nomeItem(i)} · resta {(i.qtd_planejada - i.qtd_executada).toFixed(0)} {i.unidade || "un"}</option>)}
                </select>
                {selectedOs && itens.length === 0 && (
                  <div style={{ fontSize: "0.8rem", color: "#B45309", marginTop: 8 }}>
                    Esta OS ainda não tem itens de categoria <strong>Vertical</strong>. Adicione um item de placa primeiro.
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* PASSO 2 — Categoria CONTRAN */}
        {step === 2 && (
          <div className="pcp-card" style={{ padding: 20 }}>
            <div className="overline mb-1">Passo 2</div>
            <h2 className="font-display font-bold text-lg" style={{ marginBottom: 16 }}>Qual grupo de placa?</h2>
            <div style={{ display: "grid", gap: 10 }}>
              {CATEGORIAS_CONTRAN.map((c) => (
                <button
                  key={c.key}
                  onClick={() => { setCategoria(c); setPlaca(c.sem_codigo ? c.placas[0] : null); setTamanhoPadrao(""); setDims({ diametro_cm: "", lado_cm: "", largura_cm: "", altura_cm: "" }); }}
                  data-testid={`cat-${c.key}`}
                  style={{
                    padding: "16px 18px",
                    border: categoria?.key === c.key ? `3px solid ${c.cor}` : "1px solid #E4E4E7",
                    background: categoria?.key === c.key ? c.cor_fundo : "#FFF",
                    color: "#0A0A0A",
                    borderRadius: 6, cursor: "pointer", textAlign: "left",
                    display: "flex", alignItems: "center", gap: 14, minHeight: 68,
                  }}
                >
                  <div style={{
                    width: 14, height: 44, borderRadius: 3, background: c.swatch || c.cor, border: c.swatch ? `2px solid ${c.cor}` : "none", flexShrink: 0,
                  }} />
                  <div>
                    <div style={{ fontSize: "1.05rem", fontWeight: 700 }}>{c.label}</div>
                    <div style={{ fontSize: "0.78rem", color: "#71717A" }}>{c.sem_codigo ? `${c.descricao} · sem código, direto para tamanho` : `${c.placas.length} placas catalogadas`}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* PASSO 3 — Código da placa */}
        {step === 3 && categoria && (
          <div className="pcp-card" style={{ padding: 20 }}>
            <div className="overline mb-1">Passo 3 · {categoria.label}</div>
            <h2 className="font-display font-bold text-lg" style={{ marginBottom: 12 }}>Escolha o código</h2>
            <input
              className="input input-lg"
              placeholder="Buscar código ou nome (ex.: R-19, curva, pedestre)"
              value={filtro} onChange={(e) => setFiltro(e.target.value)}
              data-testid="ap-pl-busca"
              style={{ marginBottom: 12 }}
            />
            <div style={{
              display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
              gap: 8, maxHeight: "60vh", overflowY: "auto", paddingRight: 4,
            }}>
              {placasFiltradas.map((p) => {
                const sel = placa?.codigo === p.codigo;
                return (
                  <button
                    key={p.codigo}
                    onClick={() => { setPlaca(p); setTamanhoPadrao(""); setDims({ diametro_cm: "", lado_cm: "", largura_cm: "", altura_cm: "" }); }}
                    data-testid={`placa-${p.codigo}`}
                    style={{
                      padding: 10,
                      border: sel ? "2px solid #0A0A0A" : "1px solid #E4E4E7",
                      background: sel ? "#0A0A0A" : "#FFF",
                      color: sel ? "#FFCC00" : "#0A0A0A",
                      borderRadius: 4, cursor: "pointer", textAlign: "left",
                      minHeight: 96, display: "flex", flexDirection: "column", gap: 6,
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <PlacaImg placa={p} cor={categoria.cor} corFundo={sel ? "#FFF" : categoria.cor_fundo} size={44} />
                      <div style={{ fontWeight: 800, fontSize: "0.95rem" }}>{p.codigo}</div>
                    </div>
                    <div style={{ fontSize: "0.72rem", opacity: 0.85, lineHeight: 1.25 }}>{p.nome}</div>
                  </button>
                );
              })}
              {placasFiltradas.length === 0 && (
                <div style={{ gridColumn: "1 / -1", padding: 24, textAlign: "center", color: "#71717A" }}>
                  Nenhum código encontrado para "<strong>{filtro}</strong>".
                </div>
              )}
            </div>
          </div>
        )}

        {/* PASSO 4 — Tamanho */}
        {step === 4 && placa && (
          <div className="pcp-card" style={{ padding: 20 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 4 }}>
              <PlacaImg placa={placa} cor={categoria?.cor} corFundo={categoria?.cor_fundo} size={56} />
              <div>
                <div className="overline mb-1">Passo 4 · {placa.sem_codigo ? categoria.label : placa.codigo}</div>
                <h2 className="font-display font-bold text-lg" style={{ marginBottom: 0 }}>Tamanho e quantidade</h2>
                <div style={{ fontSize: "0.78rem", color: "#71717A" }}>{placa.nome}</div>
              </div>
            </div>
            <div style={{ fontSize: "0.82rem", color: "#71717A", marginBottom: 14 }}>
              Forma: <strong style={{ color: "#0A0A0A" }}>{placa.forma}</strong>{placa.nota && <span data-testid="placa-nota"> · {placa.nota}</span>}
            </div>

            {/* Presets padrão */}
            {Object.keys(presetsTamanho).length > 0 && (
              <>
                <label className="label">Tamanho padrão Tecnovias</label>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: 8, marginBottom: 10 }}>
                  {Object.entries(presetsTamanho).map(([nome, valores]) => {
                    const sel = tamanhoPadrao === nome;
                    const rotulo = placa.forma === "circulo" ? `Ø ${valores.diametro_cm}cm`
                      : placa.forma === "retangulo" ? `${valores.largura_cm}×${valores.altura_cm}cm`
                      : `Lado ${valores.lado_cm}cm`;
                    return (
                      <button
                        key={nome}
                        onClick={() => aplicarPreset(nome, valores)}
                        data-testid={`preset-${nome}`}
                        style={{
                          padding: 12, border: sel ? "2px solid #0A0A0A" : "1px solid #E4E4E7",
                          background: sel ? "#0A0A0A" : "#FFF", color: sel ? "#FFCC00" : "#0A0A0A",
                          borderRadius: 4, cursor: "pointer", textAlign: "left",
                        }}
                      >
                        <div style={{ fontWeight: 700, fontSize: "0.85rem" }}>{nome}</div>
                        <div style={{ fontSize: "0.75rem", opacity: 0.85, marginTop: 2 }}>{rotulo}</div>
                      </button>
                    );
                  })}
                </div>
              </>
            )}

            {/* Entrada livre */}
            <label className="label" style={{ marginTop: 8 }}>Dimensões (cm)</label>
            {placa.forma === "circulo" && (
              <input type="number" inputMode="decimal" className="input input-lg" placeholder="Diâmetro (cm)"
                value={dims.diametro_cm}
                onChange={(e) => { setTamanhoPadrao("Livre"); setDims({ ...dims, diametro_cm: e.target.value }); }}
                data-testid="ap-pl-diametro"
              />
            )}
            {(placa.forma === "octogono" || placa.forma === "triangulo" || placa.forma === "losango") && (
              <input type="number" inputMode="decimal" className="input input-lg" placeholder="Lado (cm)"
                value={dims.lado_cm}
                onChange={(e) => { setTamanhoPadrao("Livre"); setDims({ ...dims, lado_cm: e.target.value }); }}
                data-testid="ap-pl-lado"
              />
            )}
            {placa.forma === "retangulo" && (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                <input type="number" inputMode="decimal" className="input input-lg" placeholder="Largura (cm)"
                  value={dims.largura_cm}
                  onChange={(e) => { setTamanhoPadrao("Livre"); setDims({ ...dims, largura_cm: e.target.value }); }}
                  data-testid="ap-pl-largura"
                />
                <input type="number" inputMode="decimal" className="input input-lg" placeholder="Altura (cm)"
                  value={dims.altura_cm}
                  onChange={(e) => { setTamanhoPadrao("Livre"); setDims({ ...dims, altura_cm: e.target.value }); }}
                  data-testid="ap-pl-altura"
                />
              </div>
            )}

            <label className="label" style={{ marginTop: 14 }}>Quantidade de placas iguais</label>
            <input type="number" min="1" step="1" inputMode="numeric" className="input input-lg"
              style={{ fontSize: "1.4rem", fontWeight: 600 }}
              value={qtdPlacas}
              onChange={(e) => setQtdPlacas(e.target.value)}
              data-testid="ap-pl-qtd"
            />

            {/* Preview de área */}
            {dimsPreenchidas() && (
              <div style={{ marginTop: 14, padding: 12, background: "#FFFBEA", border: "1px solid #FFCC00", borderRadius: 4 }}>
                <div className="overline">Área calculada</div>
                <div className="font-mono font-semibold" style={{ fontSize: "1.5rem" }}>
                  {areaTotal.toLocaleString("pt-BR", { maximumFractionDigits: 3 })} <span style={{ fontSize: "0.9rem", color: "#71717A" }}>m²</span>
                </div>
                <div style={{ fontSize: "0.75rem", color: "#52525B", marginTop: 4 }}>
                  {areaUnit.toLocaleString("pt-BR", { maximumFractionDigits: 3 })} m² por placa × {qtdN || 0}
                </div>
              </div>
            )}
          </div>
        )}

        {/* PASSO 5 — Tipo de suporte */}
        {step === 5 && placa && (
          <div className="pcp-card" style={{ padding: 20 }}>
            <div className="overline mb-1">Passo 5</div>
            <h2 className="font-display font-bold text-lg" style={{ marginBottom: 16 }}>Tipo de suporte</h2>

            <label className="label">Tipo de suporte</label>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
              {SUPORTES.map((s) => (
                <button
                  key={s.key}
                  onClick={() => escolherSuporte(s)}
                  data-testid={`sup-${s.key}`}
                  style={{
                    padding: "12px 10px", border: suporte?.key === s.key ? "2px solid #0A0A0A" : "1px solid #E4E4E7",
                    background: suporte?.key === s.key ? "#0A0A0A" : "#FFF", color: suporte?.key === s.key ? "#FFCC00" : "#0A0A0A",
                    borderRadius: 4, cursor: "pointer", fontWeight: 600, fontSize: "0.85rem",
                  }}
                >{s.label}</button>
              ))}
            </div>

            {suporte && (
              <div style={{ marginTop: 16, padding: 12, background: "#FAFAFA", border: "1px solid #E4E4E7", borderRadius: 4 }} data-testid="poste-bloco">
                <label className="label" style={{ marginBottom: 6 }}>Apontar também o poste/suporte? <span style={{ color: "#71717A", fontWeight: 400 }}>(medido como item separado)</span></label>
                <select className="select" value={posteItem} onChange={(e) => setPosteItem(e.target.value)} data-testid="poste-item">
                  <option value="">Não apontar suporte (já existente / incluso na placa)</option>
                  {itensPoste.map((i) => <option key={i.id} value={i.id}>{nomeItem(i)} · {i.unidade}{isItemPoste(i) ? "  ★" : ""}</option>)}
                </select>
                {posteItem && (
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 8 }}>
                    <span style={{ fontSize: "0.82rem" }}>Quantidade de postes:</span>
                    <input type="number" min="1" step="1" className="input" style={{ width: 90, textAlign: "center" }} value={qtdPostes} onChange={(e) => setQtdPostes(e.target.value)} data-testid="poste-qtd" />
                    <span style={{ fontSize: "0.72rem", color: "#71717A" }}>sugestão: {POSTES_POR_SUPORTE[suporte.key] || 1} p/ {suporte.label.toLowerCase()}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* PASSO 6 — Localização + Foto */}
        {step === 6 && (
          <div className="pcp-card" style={{ padding: 20 }}>
            <div className="overline mb-1">Passo 6</div>
            <h2 className="font-display font-bold text-lg" style={{ marginBottom: 12 }}>Localização e foto</h2>

            <label className="label">Km (formato: 0+000)</label>
            <input className="input input-lg" inputMode="text" placeholder="Ex.: 12+450"
              value={km} onChange={(e) => setKm(normalizeKm(e.target.value))}
              data-testid="ap-pl-km"
              style={{ fontSize: "1.4rem", fontWeight: 600, marginBottom: 14 }}
            />

            <label className="label">Lado da via</label>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 14 }}>
              {LADOS_VIA.map((l) => (
                <button
                  key={l.key}
                  onClick={() => setLadoVia(l)}
                  data-testid={`lado-${l.key}`}
                  style={{
                    padding: "14px 4px", border: ladoVia?.key === l.key ? "2px solid #0A0A0A" : "1px solid #E4E4E7",
                    background: ladoVia?.key === l.key ? "#0A0A0A" : "#FFF", color: ladoVia?.key === l.key ? "#FFCC00" : "#0A0A0A",
                    borderRadius: 4, cursor: "pointer", fontWeight: 600, fontSize: "0.82rem",
                  }}
                >{l.label}</button>
              ))}
            </div>

            <div style={{ padding: 10, background: "#FFFBEA", border: "1px solid #FFCC00", borderRadius: 3, fontSize: "0.78rem", color: "#7A5D00", marginBottom: 14 }}>
              <strong>✨ Timestamp automático:</strong> data/hora, GPS, código CONTRAN, km e lado da via são estampados na foto.
            </div>

            <div className="label" style={{ marginBottom: 6 }}>Fotos da placa <span style={{ fontWeight: 400, color: "#71717A" }}>· a foto do DEPOIS é obrigatória; as outras são opcionais</span></div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 8, marginBottom: 12 }} data-testid="ap-pl-slots-foto">
              {TIPOS_FOTO.map((t, ti) => {
                const idxs = fotosTipos.map((x, i) => (x === t.key ? i : -1)).filter((i) => i >= 0);
                return (
                  <div key={t.key} style={{ border: `2px ${idxs.length ? "solid #16A34A" : "dashed #D4D4D8"}`, borderRadius: 6, padding: 8, background: idxs.length ? "#F0FDF4" : "#FAFAFA" }} data-testid={`ap-pl-slot-${t.key}`}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                      <strong style={{ fontSize: "0.9rem" }}>{ti + 1}. {t.label}{t.key === "depois" && <span style={{ color: "#DC2626" }}> *</span>}</strong>
                      {idxs.length > 0 && <span style={{ fontSize: "0.7rem", color: "#15803D", fontWeight: 700 }}>✓ {idxs.length}</span>}
                    </div>
                    {idxs.length > 0 && (
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 4, marginBottom: 6 }}>
                        {idxs.map((i) => (
                          <div key={i} style={{ position: "relative" }}>
                            <img src={fotos[i]} alt="" style={{ width: "100%", aspectRatio: "1", objectFit: "cover", borderRadius: 4 }} />
                            <button type="button" onClick={() => { setFotos(fotos.filter((_, ix) => ix !== i)); setFotosTipos(fotosTipos.filter((_, ix) => ix !== i)); }} data-testid={`ap-pl-remover-foto-${i}`}
                              style={{ position: "absolute", top: 3, right: 3, background: "rgba(0,0,0,0.7)", color: "#FFF", border: 0, borderRadius: "50%", width: 20, height: 20, cursor: "pointer" }}><X size={11} /></button>
                          </div>
                        ))}
                      </div>
                    )}
                    <label htmlFor={`placa-photo-${t.key}`} className="btn btn-outline btn-sm btn-block" style={{ cursor: "pointer" }}>
                      <Camera size={16} weight="bold" /> {idxs.length ? "Outra" : "Tirar foto"}
                    </label>
                    <input id={`placa-photo-${t.key}`} type="file" accept="image/*" capture="environment" data-tipo={t.key} onChange={handlePhotos} style={{ display: "none" }} data-testid={`ap-pl-foto-${t.key}`} />
                  </div>
                );
              })}
            </div>
            {fotos.length > 0 && (
              <div data-testid="ap-pl-fotos-tamanho" style={{ fontSize: "0.72rem", color: "#71717A", marginTop: -6, marginBottom: 14 }}>
                {fotos.length} foto{fotos.length > 1 ? "s" : ""} · {fmtKB(fotos.reduce((s, f) => s + dataUrlBytes(f), 0))} · comprimidas para envio rápido
              </div>
            )}

            <button type="button" onClick={captureGeo} disabled={geoLoading} data-testid="ap-pl-gps"
              className="btn btn-outline btn-lg btn-block"
              style={{ background: geo ? "#ECFDF5" : "#FAFAFA", borderColor: geo ? "#059669" : undefined, color: geo ? "#065F46" : undefined }}
            >
              <MapPin size={20} weight="bold" />
              {geoLoading ? "Obtendo GPS…" : geo ? `GPS OK · ±${Math.round(geo.accuracy)}m` : "Capturar GPS do local"}
            </button>
          </div>
        )}

        {/* PASSO 7 — Revisão */}
        {step === 7 && currentItem && placa && categoria && (
          <div className="pcp-card" style={{ padding: 20 }}>
            <div className="overline mb-1">Passo 7</div>
            <h2 className="font-display font-bold text-lg" style={{ marginBottom: 16 }}>Confira e envie</h2>

            <div style={{ padding: 16, background: "#FFFBEA", border: "2px solid #FFCC00", borderRadius: 4, marginBottom: 14 }}>
              <div className="overline">Área total</div>
              <div className="font-mono font-semibold" style={{ fontSize: "2rem", lineHeight: 1.1 }}>
                {areaTotal.toLocaleString("pt-BR", { maximumFractionDigits: 3 })} <span style={{ fontSize: "1rem", color: "#71717A" }}>m²</span>
              </div>
              <div className="font-mono" style={{ fontSize: "0.78rem", color: "#52525B", marginTop: 4 }}>
                {areaUnit.toLocaleString("pt-BR", { maximumFractionDigits: 3 })} m² × {qtdN || 0} placa(s)
              </div>
            </div>

            <table className="pcp-table" style={{ marginBottom: 12 }}>
              <tbody>
                <tr><td style={{ color: "#71717A" }}>OS</td><td style={{ fontWeight: 600, textAlign: "right" }}>{ordens.find((o) => o.id === selectedOs)?.numero}</td></tr>
                <tr><td style={{ color: "#71717A" }}>Item</td><td style={{ fontWeight: 600, textAlign: "right" }}>{currentItem.descricao}</td></tr>
                <tr><td style={{ color: "#71717A" }}>Categoria</td><td style={{ fontWeight: 600, textAlign: "right" }}>{categoria.label}</td></tr>
                {!placa.sem_codigo && <tr><td style={{ color: "#71717A" }}>Código</td><td style={{ fontWeight: 700, textAlign: "right" }}>{placa.codigo} · {placa.nome}</td></tr>}
                <tr><td style={{ color: "#71717A" }}>Tamanho</td><td style={{ fontWeight: 600, textAlign: "right" }}>{tamanhoPadrao || "Livre"}</td></tr>
                <tr><td style={{ color: "#71717A" }}>Quantidade</td><td style={{ fontWeight: 600, textAlign: "right" }}>{qtdN} placa(s)</td></tr>
                <tr><td style={{ color: "#71717A" }}>Suporte</td><td style={{ fontWeight: 600, textAlign: "right" }}>{suporte?.label}</td></tr>
                <tr><td style={{ color: "#71717A" }}>Localização</td><td style={{ fontWeight: 600, textAlign: "right" }}>km {km} · {ladoVia?.label}</td></tr>
                <tr><td style={{ color: "#71717A" }}>Fotos</td><td style={{ fontWeight: 600, textAlign: "right" }}>{fotos.length}</td></tr>
                <tr><td style={{ color: "#71717A" }}>GPS</td><td style={{ fontWeight: 600, textAlign: "right" }}>{geo ? "OK" : "Não capturado"}</td></tr>
              </tbody>
            </table>

            <label className="label">Observação (opcional)</label>
            <textarea className="textarea" rows={2} value={observacao} onChange={(e) => setObservacao(e.target.value)} placeholder="Ex.: substituição por avaria, poste novo" />
          </div>
        )}

        {/* Navegação */}
        {step < 7 && !canNext() && missingReason() && (
          <div data-testid="ap-pl-missing-hint" style={{
            marginTop: 12, padding: "10px 14px", background: "#FEF2F2", border: "1px solid #FCA5A5",
            borderRadius: 4, fontSize: "0.82rem", color: "#991B1B", display: "flex", alignItems: "center", gap: 8,
          }}>
            <span style={{ fontWeight: 700 }}>Falta:</span> {missingReason()}
          </div>
        )}
        <div className="wizard-actions" style={{ display: "flex", gap: 8, marginTop: 16 }} data-testid="pl-wizard-actions">
          {step > 1 && (
            <button className="btn btn-outline btn-lg" style={{ flex: 1 }} onClick={() => setStep(step === 4 && categoria?.sem_codigo ? 2 : step - 1)} data-testid="btn-voltar-placa">
              <ArrowLeft size={16} weight="bold" /> Voltar
            </button>
          )}
          {step < 7 && (
            <button className="btn btn-primary btn-lg" style={{ flex: 2 }} disabled={!canNext()} onClick={() => setStep(step === 2 && categoria?.sem_codigo ? 4 : step + 1)} data-testid="btn-avancar-placa">
              Avançar <ArrowRight size={16} weight="bold" />
            </button>
          )}
          {step === 7 && (
            <button className="btn btn-primary btn-lg" style={{ flex: 2 }} disabled={loading} onClick={submit} data-testid="btn-salvar-placa">
              <CheckCircle size={20} weight="bold" /> {loading ? "Salvando…" : "Salvar placa"}
            </button>
          )}
        </div>
      </div>
    </>
  );
}
